import React, { useState } from 'react';
import { Avatar, Typography, Box, Grid } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InlineSpinner from 'Components/Commons/Loader/InlineSpinner';
import Api from '../../../Helpers/ApiHandler';
import WestRoundedIcon from '@mui/icons-material/WestRounded';
import { SERVER_ERROR, VALIDATE_EMAIL } from 'Helpers/Utils';
import { API_FORGOT_PASSWORD } from 'Helpers/ApiPaths';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import CustomTextField from 'Components/FormComponents/CustomTextField';
import {
    GlobalInputLabelWrapper,
    GlobalInputLabelRequiredWrapper,
    AddButtonWrapper
} from 'Components/Global.style';
import background from '../../../Assets/Images/launch_icons.png';
import OTPModal from './OtpModal';

/**
 * @constant
 * @description API handler instance for making HTTP requests.
 *
 * @type {Api}
 */
const API = new Api({});

/**
 * @component
 * @description Main forgot password component for password recovery.
 * Provides comprehensive password reset functionality including:
 * - Email validation and submission
 * - OTP verification integration
 * - Two-step password recovery process
 * - Form validation with Yup schema
 * - Loading states and error handling
 * - Responsive design with brand imagery
 * - Navigation between email and OTP steps
 * - Professional UI with Material-UI components
 *
 * Key Features:
 * - Email-based password recovery
 * - OTP verification modal integration
 * - Form validation with real-time feedback
 * - Two-step recovery process (email → OTP)
 * - Loading states during API calls
 * - Error handling with toast notifications
 * - Responsive two-column layout
 * - Brand imagery and logo display
 * - Navigation controls between steps
 * - Professional authentication interface
 * - Seamless OTP modal integration
 *
 * @returns {JSX.Element} Complete forgot password interface
 *
 * @example
 * // Used in routing configuration
 * import ForgotPassword from 'Components/Pages/ForgotPassword/ForgotPassword';
 *
 * <Route path="/forgot-password" element={<ForgotPassword />} />
 *
 * @example
 * // Component usage in authentication flow
 * function AuthenticationApp() {
 *   return (
 *     <div>
 *       <ForgotPassword />
 *     </div>
 *   );
 * }
 *
 * @see API_FORGOT_PASSWORD - Forgot password API endpoint
 * @see OTPModal - OTP verification modal component
 * @see VALIDATE_EMAIL - Email validation regex
 * @see CustomTextField - Styled text field component
 */
export default function ForgotPassword() {
    // const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [otpModalShow, setOtpModalShow] = useState(false);
    const [email, setEmail] = useState('');

    /**
     * @constant
     * @description Initial form values for forgot password form.
     * Contains default empty value for email field.
     *
     * @type {Object}
     * @property {string} email - User email address
     */
    const initialValues = {
        email: ''
    };

    /**
     * @constant
     * @description Yup validation schema for forgot password form.
     * Validates email format and required field constraints.
     *
     * @type {yup.ObjectSchema}
     */
    const checkoutSchema = yup.object().shape({
        email: yup
            .string()
            .strict()
            .trim('Cannot include leading and trailing space')
            .required('Email is required')
            .matches(VALIDATE_EMAIL, 'Please enter a valid email')
            .max(150, 'Email must not exceed 150 characters.')
    });

    /**
     * @function
     * @description Handles form submission for forgot password request.
     * Sends password reset email and transitions to OTP verification step.
     *
     * @param {Object} values - Form values from Formik
     * @param {string} values.email - User email address
     * @returns {void}
     *
     * @example
     * // Called when user submits forgot password form
     * handleSubmit({ email: 'user@example.com' });
     */
    const handleSubmit = (values) => {
        setLoading(true);
        setEmail(values.email.replace(/\s/g, ''));
        const payload = {
            email: `${values.email.replace(/\s/g, '')}`
        };
        const data = [];
        data['data'] = payload;

        API.post(`${API_FORGOT_PASSWORD}`, data)
            .then((response) => {
                setLoading(false);
                setOtpModalShow(true);
                // navigate(URL_LOGIN);
                toast.success(response.message);
            })
            .catch((error) => {
                setLoading(false);
                toast.error(error?.response?.data?.message || SERVER_ERROR);
            });
    };

    return (
        <Grid container component="main" sx={{ height: '100vh', backgroundColor: '#F5E0B5' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexGrow: 1,
                    flexBasis: '50%',
                    height: '100%' // Ensure the parent container has a height defined
                }}>
                <img
                    loading="lazy"
                    decoding="async"
                    src={background}
                    alt="Bluecow Divine Connect"
                    style={{
                        maxWidth: '60%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                        borderRadius: '12px'
                    }}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    flexBasis: '50%',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                <Box
                    sx={{
                        my: 6,
                        mx: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#FFFFFF',
                        padding: '20px',
                        height: '490px',
                        borderRadius: '5px',
                        position: 'relative'
                    }}>
                    {otpModalShow && (
                        <WestRoundedIcon
                            style={{ position: 'absolute', left: 18 }}
                            onClick={() => {
                                setEmail('');
                                setOtpModalShow(false);
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    )}

                    <Avatar sx={{ m: 1, bgcolor: '#F5E0B5' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        {otpModalShow ? 'OTP verification' : 'Forgot Password'}
                    </Typography>
                    {otpModalShow ? (
                        <OTPModal email={email} setOtpModalShow={setOtpModalShow} />
                    ) : (
                        <Formik
                            onSubmit={handleSubmit}
                            initialValues={initialValues}
                            validationSchema={checkoutSchema}
                            validateOnChange={true}
                            validateOnBlur={false}>
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box style={{ marginTop: '20px', width: '500px' }}>
                                        <GlobalInputLabelWrapper>
                                            Email{' '}
                                            <GlobalInputLabelRequiredWrapper>
                                                {' '}
                                                *{' '}
                                            </GlobalInputLabelRequiredWrapper>
                                        </GlobalInputLabelWrapper>
                                        <CustomTextField
                                            fullWidth
                                            name="email"
                                            type="text"
                                            value={values.email}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            error={!!touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                        />

                                        <AddButtonWrapper
                                            type="submit"
                                            fullWidth
                                            disabled={loading}
                                            sx={{ mt: 3, mb: 2 }}>
                                            {loading ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1
                                                    }}>
                                                    <InlineSpinner size={22} color="inherit" />
                                                    Submitting…
                                                </Box>
                                            ) : (
                                                'Submit'
                                            )}
                                        </AddButtonWrapper>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    )}
                </Box>
            </Box>

        </Grid>
    );
}
